import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage, showAlertWarning } from '@utils/Alerts/Message';
import { hideLoader, showLoader } from '@utils/Loader';

/**
 * Crear una plantilla de WhatsApp y mandarla a aprobación de Meta.
 *
 * ⚠️ **Las variables se escriben como `{{1}}`, `{{2}}`… y su NÚMERO es lo que
 * importa.** Al vincular la plantilla a un aviso, el server compara ese número
 * contra lo que el código realmente manda y se niega si no coincide: con el
 * número equivocado el mensaje **no se envía a medias, se cancela**.
 *
 * ⚠️ **La categoría no es un detalle burocrático.** `UTILITY` es para avisos de
 * algo que el cliente pidió (su pedido); `MARKETING` es promoción, **se cobra
 * más caro** y Meta es más estricta al aprobarla.
 */

const CATEGORIAS = [
    { value: 'UTILITY', label: 'Utilidad — avisos de un pedido que el cliente hizo' },
    { value: 'MARKETING', label: 'Marketing — promociones (se cobra más caro)' }
];

interface ModalNewTemplateProps {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const ModalNewTemplate: React.FunctionComponent<ModalNewTemplateProps> = ({ open, onClose, onCreated }) => {
    const [friendlyName, setFriendlyName] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [buttonTitle, setButtonTitle] = useState<string>('Detalles del pedido');
    const [buttonUrl, setButtonUrl] = useState<string>('https://app.krispis.mx/order-details?orderId={{1}}');
    const [category, setCategory] = useState<string>('UTILITY');
    const [saving, setSaving] = useState<boolean>(false);

    /** Cuántos `{{n}}` distintos hay entre el texto y la URL del botón. */
    const variables = useMemo(() => {
        const encontradas = new Set<string>();
        const buscar = (texto: string) => {
            const matches = texto.match(/\{\{\s*(\d+)\s*\}\}/g) || [];
            matches.forEach((match) => encontradas.add(match.replace(/[^\d]/g, '')));
        };
        buscar(body);
        buscar(buttonUrl);
        return Array.from(encontradas).sort((a, b) => Number(a) - Number(b));
    }, [body, buttonUrl]);

    /**
     * Los números tienen que ser 1..N sin huecos: Twilio numera las variables por
     * posición y un `{{1}}` con `{{3}}` deja un hueco que nadie va a llenar.
     */
    const huecos = useMemo(() => variables.some((value, index) => Number(value) !== index + 1), [variables]);

    const limpiar = () => {
        setFriendlyName('');
        setBody('');
        setButtonTitle('Detalles del pedido');
        setButtonUrl('https://app.krispis.mx/order-details?orderId={{1}}');
        setCategory('UTILITY');
    };

    const crear = async () => {
        if (!friendlyName.trim() || !body.trim()) {
            showAlertError('Ponle nombre y escribe el mensaje');
            return;
        }
        if (huecos) {
            showAlertError('Las variables deben ir numeradas 1, 2, 3… sin saltarse ninguna');
            return;
        }
        setSaving(true);
        showLoader();
        try {
            // Twilio pide un EJEMPLO por variable: es lo que Meta ve al revisar.
            const ejemplos: Record<string, string> = {};
            variables.forEach((numero) => {
                ejemplos[numero] = `ejemplo${numero}`;
            });
            const { data } = await axiosPrivate.post('/twilio/templates', {
                friendlyName: friendlyName.trim().toLowerCase().replace(/\s+/g, '_'),
                language: 'es_MX',
                body: body.trim(),
                variables: ejemplos,
                buttonTitle: buttonTitle.trim() || undefined,
                buttonUrl: buttonUrl.trim() || undefined,
                category
            });
            if (data?.submitted) {
                showAlertMessage('Plantilla creada y enviada a Meta. Queda en revisión.');
            } else {
                // Se creó pero no se pudo enviar a aprobar: existe y no sirve.
                // Decirlo es lo único honesto — si no, quedaría de fantasma.
                showAlertWarning(
                    `La plantilla se creó pero no se pudo enviar a aprobación: ${data?.error || 'error desconocido'}. Bórrala y vuelve a intentar.`
                );
            }
            limpiar();
            onCreated();
        } catch (error: any) {
            showAlertError(error?.response?.data?.error || error);
        }
        hideLoader();
        setSaving(false);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ style: { borderRadius: 15 } }}>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Nueva plantilla
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="Nombre"
                        placeholder="order_paid_v3"
                        helperText="Solo para identificarla. Los espacios se cambian por guiones bajos."
                        value={friendlyName}
                        onChange={(event) => setFriendlyName(event.target.value)}
                        fullWidth
                        required
                    />
                    <TextField
                        label="Mensaje"
                        placeholder={'¡Hola {{1}}! Gracias por tu pedido en {{2}}.'}
                        value={body}
                        onChange={(event) => setBody(event.target.value)}
                        fullWidth
                        multiline
                        minRows={4}
                        required
                        helperText="Usa {{1}}, {{2}}… donde vayan los datos que cambian."
                    />
                    <TextField
                        select
                        label="Categoría"
                        value={category}
                        onChange={(event) => setCategory(event.target.value)}
                        fullWidth
                    >
                        {CATEGORIAS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Typography variant="subtitle2" fontWeight={600}>
                        Botón (opcional)
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Texto del botón"
                            value={buttonTitle}
                            onChange={(event) => setButtonTitle(event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="A dónde lleva"
                            value={buttonUrl}
                            onChange={(event) => setButtonUrl(event.target.value)}
                            fullWidth
                        />
                    </Stack>

                    {variables.length > 0 && (
                        <Alert severity={huecos ? 'error' : 'info'}>
                            {huecos
                                ? 'Las variables deben ir numeradas 1, 2, 3… sin saltarse ninguna.'
                                : `Esta plantilla tendrá ${variables.length} variable(s). Al vincularla a un aviso, ese número tiene que coincidir con lo que el sistema manda.`}
                        </Alert>
                    )}

                    <Alert severity="warning">
                        Una vez que Meta la apruebe, <strong>este texto ya no se puede cambiar</strong>. Revísalo bien
                        antes de enviarla.
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="primary">
                    Cancelar
                </Button>
                <Button variant="contained" color="primary" onClick={crear} disabled={saving}>
                    Crear y enviar a Meta
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalNewTemplate;
